import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography
} from '@mui/material'
import React, { FC, MouseEvent, useCallback, useContext, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { AppRoutes } from '../../models'
import { AuthContext } from '../../providers'
import { useUnlinkCustomerFromVehicleMutation } from '../../redux/api'
import { VehicleEntry } from '../../sdk'
import { describeVehicle } from '../../utils'
import { loggingAssert } from '../../utils/handleError/loggingAssert'
import { ClickableDeletableCard, ErrorMessageWrapper } from '../SharedUI'

interface Props {
  vehicles: VehicleEntry[]
}

export const VehiclesByCustomer: FC<Props> = ({ vehicles }) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const navigate = useNavigate()
  const { id: customerId } = useParams()
  const { currentUser, userShopId } = useContext(AuthContext)
  const shopId = loggingAssert('ShopId not set', userShopId)
  const accessToken = loggingAssert('Token not set', currentUser!.accessToken)
  const [showDeleteVehicleDialog, setShowDeleteVehicleDialog] = useState(false)
  const [deleteVehicle, setDeleteVehicle] = useState<VehicleEntry | undefined>()

  const [unlinkCustomerFromVehicle, { error, isLoading }] =
    useUnlinkCustomerFromVehicleMutation()
  const handleCloseDialog = useCallback(() => {
    setDeleteVehicle(undefined)
    setShowDeleteVehicleDialog(false)
  }, [])

  const handleDeleteVehicle = useCallback(async () => {
    if (!deleteVehicle) {
      return
    }
    await unlinkCustomerFromVehicle({
      authorization: accessToken,
      shop: shopId,
      customer: customerId!,
      vehicle: deleteVehicle!.id!
    })
    handleCloseDialog()
  }, [
    accessToken,
    customerId,
    deleteVehicle,
    handleCloseDialog,
    shopId,
    unlinkCustomerFromVehicle
  ])

  return (
    <ErrorMessageWrapper errorMessage={error} loading={isLoading}>
      <Menu
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}>
        <MenuItem
          data-testid="PartOverrideOptionsMenuDelete"
          onClick={() => {
            setMenuAnchor(null)
            setShowDeleteVehicleDialog(true)
          }}>
          Unlink Vehicle
        </MenuItem>
      </Menu>
      {deleteVehicle && (
        <Dialog open={showDeleteVehicleDialog} onClose={handleCloseDialog}>
          <DialogTitle>Unlink Vehicle</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to unlink this vehicle?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleDeleteVehicle}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Grid
        container
        spacing={2}
        aria-label="vehiclesContainer"
        direction="row">
        {vehicles.map((vehicle, index) => (
          <Grid item key={index}>
            <>
              <ClickableDeletableCard
                aria-label="vehicleCard"
                label={
                  <Box alignItems={'flex-start'}>
                    <Typography
                      data-testid={'vehicleName' + index}
                      sx={{
                        width: '150px',
                        boxSizing: 'border-box',
                        wordBreak: 'break-all',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                      {describeVehicle(vehicle.info!)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        width: '150px',
                        boxSizing: 'border-box',
                        wordBreak: 'break-all',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                      {vehicle.info!.licensePlate || ' '}
                    </Typography>
                  </Box>
                }
                onDelete={(event: MouseEvent<HTMLElement>) => {
                  setDeleteVehicle(vehicle)
                  setMenuAnchor(event.currentTarget)
                }}
                deleteIcon={
                  <IconButton aria-label="deleteVehicleButton">
                    <MoreVertIcon />
                  </IconButton>
                }
                icon={<DirectionsCarIcon />}
                onClick={() => navigate(AppRoutes.VEHICLES + '/' + vehicle.id)}
              />
            </>
          </Grid>
        ))}
      </Grid>
    </ErrorMessageWrapper>
  )
}
